import { sendTrack } from '@onlive.site/front-core';
import api from 'api';
import {
    AircallDialError,
    aircallDebugLog,
    collectMediaDiagnostics,
    maskPhoneNumber,
    reportAircallDialError,
    reportAircallWarning,
} from './aircallDiagnostics';

const TRACKED_EVENTS = ['outgoing_call', 'outgoing_answered', 'call_ended', 'comment_saved', 'external_dial', 'redirect_event'];

// An outbound call that ends unanswered this quickly almost certainly never got
// audio: the agent hears no ringback and the call hangs up. A callee who simply
// does not pick up keeps it ringing well past this.
const DROPPED_CALL_THRESHOLD_MS = 10000;

let latestPayload = {};
let listenersAttached = false;
// Outbound calls seen starting, by Aircall call id, until they end.
const outgoingCalls = new Map();

export function callService(phone, payload) {
    latestPayload = payload;

    // Passing no phone at all is the toolbar button: it just opens the panel.
    // An empty string is a different thing entirely, a contact row whose phone
    // field is blank, and until now it silently opened the panel as if nothing
    // had gone wrong.
    const wantsDial = phone !== null && phone !== undefined;
    const dialablePhone = String(phone ?? '').trim();

    if (!window.airCallPanel) {
        reportAircallDialError({
            reason: AircallDialError.NOT_MOUNTED,
            rawPhone: phone,
            context: { ...payload, detail: 'airCallPanel missing on window' },
        });
        return;
    }

    window.airCallPanel.setCallContext(payload ?? {});
    attachListeners();

    if (!wantsDial) {
        aircallDebugLog('opening panel without dialing');
        window.airCallPanel.show();
        return;
    }

    if (!dialablePhone) {
        reportAircallDialError({
            reason: AircallDialError.MISSING_NUMBER,
            rawPhone: phone,
            context: { ...payload, detail: 'call site provided a blank phone number' },
        });
        return;
    }

    sendTrack('aircall_dial_attempt', { ...payload, phone: maskPhoneNumber(dialablePhone) });
    window.airCallPanel.call(dialablePhone);
}

function attachListeners() {
    if (listenersAttached) return;
    listenersAttached = true;
    TRACKED_EVENTS.forEach((event) => {
        window.airCallPanel.listen(event, null, (data, evt) => sendEventTrack(data, evt, latestPayload));
    });
}

function trackCallOutcome(data, event, payload) {
    const callId = data?.call_id?.toString();
    if (!callId) return;

    if (event === 'outgoing_call') {
        outgoingCalls.set(callId, { startedAt: Date.now(), answered: false, to: data.to });
        return;
    }
    if (event === 'outgoing_answered') {
        const call = outgoingCalls.get(callId);
        if (call) call.answered = true;
        return;
    }
    if (event !== 'call_ended') return;

    // Incoming calls end here too; only outbound calls seen starting are judged.
    const call = outgoingCalls.get(callId);
    if (!call) return;
    outgoingCalls.delete(callId);

    const elapsedMs = Date.now() - call.startedAt;
    if (call.answered || elapsedMs >= DROPPED_CALL_THRESHOLD_MS) return;

    reportDroppedCall({ callId, to: call.to, elapsedMs, duration: data.duration, payload });
}

// A warning, not a dial error: the agent already knows the call dropped, what
// is missing is the trace in Sentry that says it happened and why it might have.
async function reportDroppedCall({ callId, to, elapsedMs, duration, payload }) {
    const media = await collectMediaDiagnostics();
    reportAircallWarning({
        reason: AircallDialError.CALL_DROPPED,
        dialedPhone: to,
        context: { ...payload, callId, elapsedMs, aircallDuration: duration ?? null, ...media },
    });
}

function sendEventTrack(data, event, payload) {
    aircallDebugLog(`workspace event: ${event}`, data);
    trackCallOutcome(data, event, payload);

    if (event === 'call_ended') {
        let duration = data?.duration;
        //check if duration is string
        if (typeof duration === 'string') {
            duration = duration.split(':').reduce((acc, time) => 60 * acc + +time);
        }
        sendTrack(`aircall_${event}`, { ...payload, ...data, duration });
        return;
    }
    if (event === 'outgoing_answered') {
        api.post('calls',
            {
                startedAt: Date.now(),
                direction: 'outbound',
                externalId: data.call_id?.toString(),
                provider: 'aircall',
            });
    }
    sendTrack(`aircall_${event}`, { ...payload, ...data });
}
